import cn from "classnames"
import { Fragment } from "react"

import { Flex, Row, Tooltip } from "@/atoms"
import { NAMES_ROUTES } from "@/const/routes.constants"
import { TEASERS } from "@/const/teaser.constants"
import { isSelfHosted } from "@/helpers/self-hosted.helper"
import { useLogger } from "@/hooks/useLogger"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useTranslation } from "@/hooks/useTranslation"
import { useFeatureFlags } from "@/hooks/useUser"
import { IntegrationInstallationStatus } from "@/icons"
import { ExternalLinkIcon } from "@/molecules/ExternalLinkIcon"
import { HeadingCard } from "../../molecules/HeadingCard"
import { StatisticCard } from "../../molecules/StatisticCard"
import CardDashboardTemplate from "../../templates/CardDashboardTemplate"
import { getDemoCard } from "./components/DemoCard"
import { EMPTY_VALUE_STATISTIC_CARD, getStatisticCardProps } from "./helpers"

import styles from "./InventorySnapshotWidget.module.scss"

import type { InventorySnapshotWidgetProps } from "."

export const InventorySnapshotWidget = (
  props: InventorySnapshotWidgetProps
) => {
  const prefixKeyTranslationWidget = "dashboard.inventorySnapshotWidget."
  const { t } = useTranslation()
  const { abbreviateNumber } = useNumberConvertor()
  const { logger, EVENTS } = useLogger()
  const { response, isLoading } = props
  const {
    cluster,
    application,
    containerImage,
    packages,
    branch,
    containerBaseImage
  } = response || {}
  const dataReadinessFF = useFeatureFlags("dataReadiness")
  const showPlaceholder = !!dataReadinessFF
  const loading = showPlaceholder ? false : isLoading
  const noData = showPlaceholder
    ? false
    : !cluster && !application && !containerImage && !packages

  const isDemo = props.isDemo

  const classesStatisticCard = {
    container: cn(styles.statisticCard, isDemo && styles.statisticCardDemo)
  }
  const branchIntegrationEnabled =
    branch?.total !== -1 && dataReadinessFF !== "setup_required"
  const statisticCardAttr = { t, showPlaceholder, abbreviateNumber }

  const Wrapper = isDemo ? Row : CardDashboardTemplate
  const demoProps = { isDemo, loading }

  const title = t(`${prefixKeyTranslationWidget}title`)
  const contentPopover = t(`${prefixKeyTranslationWidget}description`)
  const Container = isDemo ? Flex : Fragment
  const containerProps = isDemo
    ? { vertical: true, gap: 8, className: styles.wrapDemo }
    : {}
  const gutter = isDemo ? 16 : 8
  const { DISCOVERY, INTEGRATIONS } = NAMES_ROUTES
  const {
    ROOT: DISCOVERY_ROOT,
    APPLICATIONS,
    IMAGES,
    BASE_IMAGES,
    SBOM,
    BRANCH
  } = DISCOVERY
  const selfHosted = isSelfHosted()

  return (
    <Container {...containerProps}>
      {isDemo && <HeadingCard title={title} contentPopover={contentPopover} />}
      <Wrapper
        wrap
        loading={loading}
        noData={noData}
        title={title}
        contentPopover={contentPopover}
        gutter={[gutter, gutter]}
        teasers={{
          kortexInstalled: TEASERS.KORTEX_INSTALLED.noActiveSensorsDeployed(t),
          scmInstalled: TEASERS.SCM_INSTALLED.codeRepoNotConnected(t)
        }}
      >
        <StatisticCard
          span={8}
          classes={classesStatisticCard}
          {...getDemoCard({
            title: t("general.cluster_other"),
            data: getStatisticCardProps(cluster, statisticCardAttr),
            ...demoProps
          })}
        />
        <StatisticCard
          span={8}
          classes={classesStatisticCard}
          onClick={() => {
            logger.info(
              EVENTS.ANALYTIC_EVENTS.DASHBOARD.CLICK_INVENTORY_SNAPSHOT
                .APPLICATIONS
            )
          }}
          {...getDemoCard({
            to: selfHosted ? undefined : `/${DISCOVERY_ROOT}/${APPLICATIONS}`,
            title: t("general.application_other"),
            data: getStatisticCardProps(application, statisticCardAttr),
            ...demoProps
          })}
        />
        <Tooltip
          title={
            !branchIntegrationEnabled && (
              <div className={styles.externalLink}>
                <span>
                  {t("dashboard.inventorySnapshotWidget.branchExternalLink")}
                </span>
                <ExternalLinkIcon href={`/${INTEGRATIONS.ROOT}`} />
              </div>
            )
          }
        >
          <StatisticCard
            span={8}
            classes={classesStatisticCard}
            onClick={() => {
              logger.info(
                EVENTS.ANALYTIC_EVENTS.DASHBOARD.CLICK_INVENTORY_SNAPSHOT
                  .CODE_REPOSITORIES
              )
            }}
            formatter={(value) =>
              branchIntegrationEnabled ? (
                value
              ) : (
                <IntegrationInstallationStatus />
              )
            }
            {...getDemoCard({
              to: branchIntegrationEnabled
                ? `/${DISCOVERY_ROOT}/${BRANCH}`
                : undefined,
              title: t("general.code_repositories"),
              data: getStatisticCardProps(branch, statisticCardAttr),
              ...demoProps
            })}
          />
        </Tooltip>
        <StatisticCard
          span={8}
          classes={classesStatisticCard}
          onClick={() => {
            logger.info(
              EVENTS.ANALYTIC_EVENTS.DASHBOARD.CLICK_INVENTORY_SNAPSHOT.IMAGES
            )
          }}
          {...getDemoCard({
            to: `/${DISCOVERY_ROOT}/${IMAGES}`,
            title: t("general.image_other"),
            data: getStatisticCardProps(containerImage, statisticCardAttr),
            ...demoProps
          })}
        />
        <StatisticCard
          span={8}
          classes={classesStatisticCard}
          onClick={() => {
            logger.info(
              EVENTS.ANALYTIC_EVENTS.DASHBOARD.CLICK_INVENTORY_SNAPSHOT.IMAGES
            )
          }}
          {...getDemoCard({
            to: selfHosted ? undefined : `/${DISCOVERY_ROOT}/${BASE_IMAGES}`,
            title: t("general.base_image_other"),
            data: getStatisticCardProps(containerBaseImage, statisticCardAttr),
            ...demoProps
          })}
        />
        <StatisticCard
          span={8}
          classes={classesStatisticCard}
          onClick={() => {
            logger.info(
              EVENTS.ANALYTIC_EVENTS.DASHBOARD.CLICK_INVENTORY_SNAPSHOT.PACKAGES
            )
          }}
          {...getDemoCard({
            to: `${DISCOVERY_ROOT}/${SBOM}`,
            title: t("general.package_other"),
            data: {
              value:
                !showPlaceholder && packages?.total
                  ? abbreviateNumber(packages.total)
                  : EMPTY_VALUE_STATISTIC_CARD
            },
            ...demoProps
          })}
        />
      </Wrapper>
    </Container>
  )
}
